import React from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { GraduationCapIcon } from 'lucide-react';
import { RequestInformationPage } from './RequestInformationPage';
import { useMetaTags } from '../hooks/useMetaTags';
import { areaStudyLandings, getAreaStudyLanding } from '../data/areaStudyLandings';

// /areas-of-study/<area-slug>/<version> — an area-specific version of the
// streamlined v1 request-information flow. The area is fixed by the URL, so
// the first step asks for degree level and program instead.
//
// A version can also be configured as a bridge page (`ctaLanding`), which
// replaces the in-hero form with a call-to-action card that hands the visitor
// to the form version with its answers pre-selected.
export function AreaOfStudyLandingPage() {
  const params = useParams<{
    areaSlug?: string;
    version?: string;
  }>();
  const location = useLocation();
  const navigate = useNavigate();
  // The preview's route list can render this page without params; fall back
  // to parsing the path, then to the first configured landing.
  const [,, pathArea, pathVersion] = location.pathname.split('/');
  const areaSlug = params.areaSlug || pathArea;
  const version = params.version || pathVersion;
  const landing = getAreaStudyLanding(areaSlug, version) || Object.values(areaStudyLandings)[0];
  const cta = landing.ctaLanding;
  // Hooks must run on every render, so the meta tags are set before the
  // bridge/form branch below.
  useMetaTags({
    title: landing.content.metaTitle || 'Find Your Program | FindOnlineDegrees',
    description: landing.content.metaDescription || '',
    ogImage: landing.content.heroBackgroundImage,
    robots: cta ? 'noindex, follow' : undefined
  });
  if (!cta) {
    return <RequestInformationPage variant={`area-${areaSlug}-${version}`} lockedAreaId={landing.areaId} content={landing.content} data-id="element-1212" data-mp="125gis6o2k47u" />;
  }

  // Carry the landing URL's tracking params through, then pin the program and
  // degree level so the form opens on the first unanswered question.
  const handleCta = () => {
    const search = new URLSearchParams(location.search);
    search.set('mProgram', '300');
    search.set('degreeLevel', '3');
    navigate(`${cta.destination}?${search.toString()}`);
    window.scrollTo(0, 0);
  };
  return (
    // The app layout already supplies the site nav and footer.
    <div className="bg-white" data-id="element-1213" data-mp="35fa4k4zof6b1">
        <section className="relative border-b-4 border-[#e8594f]" data-id="element-1214" data-mp="35f08q0rya4xf">
          {landing.content.heroBackgroundImage && <>
              <img src={landing.content.heroBackgroundImage} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover" style={landing.content.heroImageFlipped ? {
            transform: 'scaleX(-1)'
          } : undefined} data-id="element-1215" data-mp="3acjs1f3i97w8" />
              <div className="absolute inset-0 bg-gray-900/40 lg:bg-gray-900/20" aria-hidden="true" data-id="element-1216" data-mp="ymzj17sckcfl" />
            </>}
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24" data-id="element-1217" data-mp="2ea4hg4w6etus">
            <div className="lg:grid lg:grid-cols-2 lg:gap-12" data-id="element-1218" data-mp="2sys7xwr60ddv">
              <div className="hidden lg:block" aria-hidden="true" data-id="element-1219" data-mp="2o5z89najzv8n" />
              <div className="bg-white rounded-2xl shadow-2xl p-8 md:p-10 text-center max-w-xl mx-auto lg:mx-0" data-id="element-1220" data-mp="2o4o4hnh3ynhj">
                <h1 className="text-3xl md:text-4xl font-bold text-gray-900 leading-tight" data-id="element-1221" data-mp="2xwkbjo169nxw">
                  {cta.cardHeadline}
                </h1>
                <p className="mt-4 text-lg font-semibold text-[#e8594f]" data-id="element-1222" data-mp="3bvjqg3yvuogv">
                  {cta.cardSubhead}
                </p>
                <button onClick={handleCta} className="mt-8 w-full sm:w-auto px-10 py-4 bg-[#e8594f] text-white text-lg font-bold rounded-lg shadow-md hover:bg-[#d54a41] hover:shadow-lg transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#e8594f]" data-id="element-1223" data-mp="36v5sjeqzwuwk">
                  {cta.ctaLabel}
                </button>
                {/* Paid-placement disclosure, kept quiet so it reads as a
                    label rather than competing with the CTA. */}
                <p className="mt-6 text-right text-[11px] text-gray-400" data-id="element-1224" data-mp="1sxx6g7kmw6tq">
                  Sponsored Ad
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20" data-id="element-1225" data-mp="1086qdszfyuuz">
          <div className="max-w-2xl" data-id="element-1226" data-mp="157t19vrsrz0m">
            <span className="mx-auto sm:mx-0 mb-5 flex h-12 w-12 items-center justify-center rounded-xl bg-[#e8594f]/10" aria-hidden="true" data-id="element-1227" data-mp="156rpfp2yj8hu">
              <GraduationCapIcon className="h-6 w-6 text-[#e8594f]" data-id="element-1228" data-mp="cjxb79wqh5ct" />
            </span>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-5" data-id="element-1229" data-mp="hhp0rx9wofdu">
              {cta.sectionHeading}
            </h2>
            <div className="space-y-5" data-id="element-1230" data-mp="2ur70kt4ygr4h">
              {cta.paragraphs.map(paragraph => <p key={paragraph.slice(0, 40)} className="text-lg text-gray-600 leading-relaxed" data-id="element-1231" data-mp="2qry3beapib46">
                  {paragraph}
                </p>)}
            </div>
          </div>

          {cta.highlights && <dl className="mt-12 grid gap-8 sm:grid-cols-3 sm:gap-10" data-id="element-1232" data-mp="2btw0j6bi1tnz">
              {cta.highlights.map(item => <div key={item.label} className="border-t-2 border-[#e8594f] pt-5" data-id="element-1233" data-mp="27249kuofllxb">
                  <dt className="flex items-baseline gap-2" data-id="element-1234" data-mp="22yy4hqd220sf">
                    <span className="text-3xl font-extrabold text-gray-900 tracking-tight" data-id="element-1235" data-mp="1y3ir0ziym6wj">
                      {item.stat}
                    </span>
                    <span className="text-sm font-semibold text-gray-500 uppercase tracking-wide" data-id="element-1236" data-mp="1o4bgsl7iusdn">
                      {item.label}
                    </span>
                  </dt>
                  <dd className="mt-2 text-gray-600 leading-relaxed" data-id="element-1237" data-mp="1eisbymqhqcjf">
                    {item.body}
                  </dd>
                </div>)}
            </dl>}

          <div className="mt-14 flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:justify-between rounded-2xl bg-gray-50 border border-gray-200 p-6 sm:p-8" data-id="element-1238" data-mp="15hu947rsgb2f">
            <p className="text-lg font-semibold text-gray-900 max-w-md" data-id="element-1239" data-mp="1x88e1ehtqd1y">
              {cta.closingLine || cta.cardSubhead}
            </p>
            <button onClick={handleCta} className="w-full sm:w-auto flex-shrink-0 px-10 py-4 bg-[#e8594f] text-white text-lg font-bold rounded-lg shadow-md hover:bg-[#d54a41] hover:shadow-lg transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#e8594f]" data-id="element-1240" data-mp="227uvk04oqfgk">
              {cta.ctaLabel}
            </button>
          </div>
        </section>
    </div>
  );
}